import { useEffect, useState } from "react";
import { usePublicClient, useReadContract, useWriteContract } from "wagmi";
import { isAddress, type Abi, type Address } from "viem";
import { ERC721BadgeAbi, ERC721DropAbi, ERC721SimpleAbi } from "@atelier/abis";
import { api } from "@/lib/api";

const ABI_BY_KIND: Record<"drop" | "simple" | "badge", Abi> = {
  drop: ERC721DropAbi as Abi,
  simple: ERC721SimpleAbi as Abi,
  badge: ERC721BadgeAbi as Abi,
};

const ZERO_ADDR = "0x0000000000000000000000000000000000000000" as const;

/**
 * Owner-only panel to read + rotate ERC2981 default royalty and the
 * marketplace `contractURI` pointer. Available on Drop / Simple / Badge —
 * Atelier1of1 royalty is per-mint, not per-contract, so this panel is
 * not used there.
 *
 * The optional name/description/imageCid/bannerCid props enable the
 * "Auto-fill" button which builds an ERC-7572 metadata JSON from the
 * collection's existing fields, pins it to IPFS, and pre-fills the
 * contractURI input. The owner still has to sign `setContractURI` to
 * persist it on-chain. Useful for backfilling collections deployed
 * before the wizard emitted contract.json automatically.
 */
export function CollectionMetadataPanel({
  contractAddress,
  chainId,
  kind,
  name,
  description,
  imageCid,
  bannerCid,
}: {
  contractAddress: Address;
  chainId: number;
  kind: "drop" | "simple" | "badge";
  // tsconfig has exactOptionalPropertyTypes; the rows pass these straight
  // from the Collection blob where they're string | undefined, so the
  // signature has to mirror that explicitly.
  name: string | undefined;
  description: string | undefined;
  imageCid: string | undefined;
  bannerCid: string | undefined;
}) {
  const abi = ABI_BY_KIND[kind];
  const pc = usePublicClient({ chainId });
  const { writeContractAsync, isPending } = useWriteContract();

  // royaltyInfo on a sale of 10_000 returns (receiver, fee_in_units_of_sale)
  // — i.e. the bps directly. Probe with tokenId 1; OZ ignores the id for
  // _defaultRoyalty so this works pre-mint too.
  const { data: royalty, refetch: refetchRoyalty } = useReadContract({
    address: contractAddress,
    abi,
    functionName: "royaltyInfo",
    args: [1n, 10000n],
    chainId,
  });
  const { data: cUri, refetch: refetchContractUri } = useReadContract({
    address: contractAddress,
    abi,
    functionName: "contractURI",
    chainId,
  });

  const [recv, recvBps] =
    Array.isArray(royalty) && royalty.length === 2
      ? (royalty as [Address, bigint])
      : [ZERO_ADDR as Address, 0n];

  const [receiver, setReceiver] = useState("");
  const [bps, setBps] = useState(500);
  const [contractUri, setContractUri] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (recv !== ZERO_ADDR) setReceiver(recv);
    if (typeof recvBps === "bigint") setBps(Number(recvBps));
  }, [recv, recvBps]);
  useEffect(() => {
    if (typeof cUri === "string") setContractUri(cUri);
  }, [cUri]);

  const updateRoyalty = async () => {
    if (!isAddress(receiver)) {
      setStatus("Royalty receiver must be a valid address.");
      return;
    }
    setStatus("Confirm in wallet…");
    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi,
        functionName: "setDefaultRoyalty",
        args: [receiver as Address, bps],
        chainId,
      });
      setStatus("Confirming…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      setStatus("Royalty updated.");
      await refetchRoyalty();
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  const updateContractUri = async () => {
    setStatus("Confirm in wallet…");
    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi,
        functionName: "setContractURI",
        args: [contractUri],
        chainId,
      });
      setStatus("Confirming…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      setStatus("contractURI updated.");
      await refetchContractUri();
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  // Backfill helper: build an ERC-7572 JSON from the collection's existing
  // fields (name + image + the ones available), pin it via the worker's
  // /ipfs/json proxy, and pre-fill the input. The user still has to sign
  // setContractURI ("Save") — we never auto-write on-chain on click.
  const canAutoFill = !!(name && imageCid);
  const autoFill = async () => {
    if (!canAutoFill) return;
    setStatus("Building + pinning ERC-7572 metadata…");
    try {
      const meta: Record<string, unknown> = {
        name,
        image: `ipfs://${imageCid}`,
      };
      if (description) meta.description = description;
      if (bannerCid) meta.banner_image = `ipfs://${bannerCid}`;
      if (recv !== ZERO_ADDR) meta.fee_recipient = recv;
      if (typeof recvBps === "bigint" && recvBps > 0n) {
        meta.seller_fee_basis_points = Number(recvBps);
      }
      const { cid } = await api.uploadJson(meta);
      setContractUri(`ipfs://${cid}`);
      setStatus(`Pinned ipfs://${cid.slice(0, 12)}… — review and click Save to write on-chain.`);
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3 text-xs">
      <div className="font-semibold">Royalty + collection metadata</div>

      <div className="space-y-2">
        <div className="text-muted-foreground">
          Default royalty (ERC-2981). Marketplaces that honor it pay{" "}
          <span className="text-foreground">{Number(recvBps) / 100}%</span> of every secondary sale to{" "}
          <code className="text-foreground">
            {recv === ZERO_ADDR
              ? "(none)"
              : `${recv.slice(0, 8)}…${recv.slice(-6)}`}
          </code>
          .
        </div>
        <div className="grid gap-2 sm:grid-cols-[2fr_1fr_auto]">
          <input
            className="input h-8 text-[11px]"
            placeholder="Receiver 0x…"
            value={receiver}
            onChange={(e) => setReceiver(e.target.value)}
            disabled={isPending}
          />
          <input
            type="number"
            min={0}
            max={10000}
            className="input h-8 text-[11px]"
            placeholder="bps (500 = 5%)"
            value={bps}
            onChange={(e) => setBps(Math.max(0, Math.min(10000, Number(e.target.value) || 0)))}
            disabled={isPending}
          />
          <button
            className="btn-secondary h-8 px-3 text-xs"
            disabled={isPending || !receiver}
            onClick={() => void updateRoyalty()}
          >
            Update
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-muted-foreground">
          contractURI — read by marketplaces (OpenSea, Magic Eden) for the collection cover,
          banner and royalty fallback. Point at an IPFS JSON, or click <em>Auto-fill</em> to
          build one from this collection&apos;s existing fields.
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            className="input h-8 flex-1 min-w-[220px] text-[11px]"
            placeholder="ipfs://Qm.../metadata.json"
            value={contractUri}
            onChange={(e) => setContractUri(e.target.value)}
            disabled={isPending}
          />
          <button
            type="button"
            className="btn-secondary h-8 px-3 text-xs"
            disabled={isPending || !canAutoFill}
            title={
              canAutoFill
                ? "Build an ERC-7572 JSON from name + image + royalty and pin it"
                : "Needs a name and an image to build metadata"
            }
            onClick={() => void autoFill()}
          >
            Auto-fill
          </button>
          <button
            type="button"
            className="btn-secondary h-8 px-3 text-xs"
            disabled={isPending}
            onClick={() => void updateContractUri()}
          >
            Save
          </button>
        </div>
      </div>

      {status && <div className="text-muted-foreground">{status}</div>}
    </div>
  );
}
